
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import StarXpGate from '@/components/star/StarXpGate.vue'
import StarTabBar from '@/components/star/StarTabBar.vue'
import StarNoData from '@/components/star/StarNoData.vue'
import StarColumnTabs from '@/components/star/StarColumnTabs.vue'
import StarArticleContent from '@/components/star/StarArticleContent.vue'
import StarArticleStatement from '@/components/star/StarArticleStatement.vue'
import starZoneApi from '@/api/starZoneApi'
import { useStarTheme } from '@/utils/useStarTheme'
import { formatStarTime, formatHitCount, getNewsCover } from '@/utils/starZoneHelper'

const HISTORY_KEY = 'starHotSubjectHistory'

export default defineComponent({
  name: 'StarHotSubject',
  components: { StarXpGate, StarTabBar, StarNoData, StarColumnTabs, StarArticleContent, StarArticleStatement },
  setup() {
    const route = useRoute()
    const { colors, isDark, isLight } = useStarTheme()
    const activeTab = ref('hotSubject')

    const tabsLoading = ref(true)
    const columnTabs = ref<any[]>([])
    const activeColumnCode = ref('')

    const listLoading = ref(false)
    const detailLoading = ref(false)
    const loadingMore = ref(false)
    const isNoMore = ref(false)
    const articleList = ref<any[]>([])
    const articleDetail = ref<any>({})
    const currentIndex = ref(0)
    const historyIds = ref<string[]>([])
    const listRef = ref<HTMLElement | null>(null)

    const leftWrapStyle = computed(() => ({
      background: isLight.value ? '#fff' : '#181818',
      borderRight: isLight.value ? '1px solid #f0f0f0' : '1px solid #353535',
    }))

    const rightWrapStyle = computed(() => ({
      background: isLight.value ? '#f7f7f7' : '#1b1b1b',
    }))

    const getCover = getNewsCover

    const loadHistory = () => {
      try {
        const raw = localStorage.getItem(HISTORY_KEY)
        historyIds.value = raw ? JSON.parse(raw) : []
      } catch {
        historyIds.value = []
      }
    }

    const saveHistory = (newsId: string) => {
      if (!newsId || historyIds.value.includes(newsId)) return
      historyIds.value.push(newsId)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(historyIds.value))
    }

    const fetchArticleList = async (sortTimestamp?: number) => {
      const token = route.query.token as string
      if (!activeColumnCode.value) return

      const isAppend = !!sortTimestamp
      if (isAppend) loadingMore.value = true
      else listLoading.value = true

      try {
        const params: any = {
          columnCodes: activeColumnCode.value,
          hasContent: 0,
          dataTypes: 3,
          showPermission: 0,
          appCode: 'com.rjhy.uranus',
          limit: 20,
        }
        if (sortTimestamp) params.sortTimestamp = sortTimestamp

        const res = await starZoneApi.getColumnNewsList({
          data: params,
          headers: { token },
        })

        if (res?.code === 1 || res?.code === 0) {
          const rows = res.data || []
          if (!rows.length) {
            if (isAppend) isNoMore.value = true
            else {
              articleList.value = []
              articleDetail.value = {}
            }
            return
          }
          articleList.value = isAppend ? articleList.value.concat(rows) : rows

          if (!isAppend) {
            const initialNewsId = route.query.newsId as string
            let targetIndex = 0
            let target = rows[0]
            if (initialNewsId) {
              const idx = rows.findIndex((n: any) => String(n.newsId) === String(initialNewsId))
              if (idx >= 0) {
                targetIndex = idx
                target = rows[idx]
              }
            }
            selectArticle(target, targetIndex, initialNewsId || target.newsId)
          }
        }
      } catch (e) {
        console.error('getColumnNewsList failed', e)
      } finally {
        listLoading.value = false
        loadingMore.value = false
      }
    }

    const fetchArticleDetail = async (newsId: string) => {
      const token = route.query.token as string
      detailLoading.value = true
      try {
        const res = await starZoneApi.getNewsContent({
          data: {
            newsId,
            token,
            serverId: 200,
            applicationCode: 'com.rjhy.uranus',
          },
          headers: { token },
        })
        if (res?.code === 1 || res?.code === 0) {
          articleDetail.value = res.data || {}
          saveHistory(newsId)
        }
      } catch (e) {
        console.error('getNewsContent failed', e)
      } finally {
        detailLoading.value = false
      }
    }

    const selectArticle = (item: any, index: number, newsIdOverride?: string) => {
      currentIndex.value = index
      const newsId = newsIdOverride || item.newsId
      if (newsId) fetchArticleDetail(String(newsId))
    }

    const switchColumn = (_tab: any) => {
      isNoMore.value = false
      articleList.value = []
      articleDetail.value = {}
      currentIndex.value = 0
      fetchArticleList()
    }

    const onListScroll = (e: Event) => {
      const el = e.target as HTMLElement
      if (isNoMore.value || loadingMore.value || listLoading.value) return
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
        const last = articleList.value[articleList.value.length - 1]
        if (last?.sortTimestamp) {
          fetchArticleList(last.sortTimestamp)
        }
      }
    }

    const initColumnTabs = async () => {
      tabsLoading.value = true
      try {
        const token = route.query.token as string
        const res = await starZoneApi.getHotSubjectColumnTabs({ headers: { token } })
        if (res?.code === 1 || res?.code === 0) {
          columnTabs.value = (res.data || []).sort((a: any, b: any) => (a.sort || 0) - (b.sort || 0))
          if (columnTabs.value.length) {
            const queryCode = route.query.columnCode as string
            const matched = queryCode
              ? columnTabs.value.find((t) => t.columnCode === queryCode)
              : null
            const target = matched || columnTabs.value[0]
            activeColumnCode.value = target.columnCode
            fetchArticleList()
          }
        }
      } catch (e) {
        console.error('getHotSubjectColumnTabs failed', e)
      } finally {
        tabsLoading.value = false
      }
    }

    onMounted(() => {
      loadHistory()
      initColumnTabs()
    })

    return {
      activeTab,
      tabsLoading,
      columnTabs,
      activeColumnCode,
      listLoading,
      detailLoading,
      loadingMore,
      articleList,
      articleDetail,
      currentIndex,
      listRef,
      colors,
      isDark,
      isLight,
      leftWrapStyle,
      rightWrapStyle,
      getCover,
      formatStarTime,
      formatHitCount,
      switchColumn,
      selectArticle,
      onListScroll,
    }
  },
})
